import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  ViewChild,
  AfterViewInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { UiMoleculeInputComponent } from '../../molecules/ui-molecule-input/ui-molecule-input.component';
import { UiAtomCountrySelectComponent } from '../ui-atom-country-select/ui-atom-country-select.component';

export interface CountryCode {
  code: string;
  name: string;
  flag: string;
  dialCode: string;
}

@Component({
  selector: 'ui-atom-phone-input',
  standalone: true,
  imports: [CommonModule, FormsModule, UiMoleculeInputComponent, UiAtomCountrySelectComponent],
  templateUrl: './ui-atom-phone-input.component.html',
  styleUrls: ['./ui-atom-phone-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneInputComponent),
      multi: true,
    },
  ],
})
export class PhoneInputComponent implements ControlValueAccessor, AfterViewInit {
  @Input() placeholder: string = '';
  @Input() required: boolean = false;
  @Output() phoneChange = new EventEmitter<string>();

  @ViewChild('phoneInput') phoneInput: any;
  @ViewChild('countrySelect') countrySelect: any;

  // Propiedades para ControlValueAccessor
  protected onChange: (value: string) => void = () => {};
  protected onTouched: () => void = () => {};
  protected disabled: boolean = false;

  protected selectedCountry;

  protected phoneNumber: string = '';

  // Propiedades para la búsqueda de países
  protected countrySearchQuery: string = '';
  protected showCountryDropdown: boolean = false;
  protected filteredCountries: CountryCode[] = [];
  protected selectedCountryCode: string = '';

  // Lista completa de países con sus códigos y banderas
  protected countries: CountryCode[] = [
    { code: 'US', name: 'United States', flag: '🇺🇸', dialCode: '1' },
    { code: 'CA', name: 'Canada', flag: '🇨🇦', dialCode: '1' },
    { code: 'RU', name: 'Russia', flag: '🇷🇺', dialCode: '7' },
    { code: 'EG', name: 'Egypt', flag: '🇪🇬', dialCode: '20' },
    { code: 'ZA', name: 'South Africa', flag: '🇿🇦', dialCode: '27' },
    { code: 'GR', name: 'Greece', flag: '🇬🇷', dialCode: '30' },
    { code: 'NL', name: 'Netherlands', flag: '🇳🇱', dialCode: '31' },
    { code: 'BE', name: 'Belgium', flag: '🇧🇪', dialCode: '32' },
    { code: 'FR', name: 'France', flag: '🇫🇷', dialCode: '33' },
    { code: 'ES', name: 'Spain', flag: '🇪🇸', dialCode: '34' },
    { code: 'HU', name: 'Hungary', flag: '🇭🇺', dialCode: '36' },
    { code: 'IT', name: 'Italy', flag: '🇮🇹', dialCode: '39' },
    { code: 'RO', name: 'Romania', flag: '🇷🇴', dialCode: '40' },
    { code: 'CH', name: 'Switzerland', flag: '🇨🇭', dialCode: '41' },
    { code: 'AT', name: 'Austria', flag: '🇦🇹', dialCode: '43' },
    { code: 'GB', name: 'United Kingdom', flag: '🇬🇧', dialCode: '44' },
    { code: 'DK', name: 'Denmark', flag: '🇩🇰', dialCode: '45' },
    { code: 'SE', name: 'Sweden', flag: '🇸🇪', dialCode: '46' },
    { code: 'NO', name: 'Norway', flag: '🇳🇴', dialCode: '47' },
    { code: 'PL', name: 'Poland', flag: '🇵🇱', dialCode: '48' },
    { code: 'DE', name: 'Germany', flag: '🇩🇪', dialCode: '49' },
    { code: 'PE', name: 'Peru', flag: '🇵🇪', dialCode: '51' },
    { code: 'MX', name: 'Mexico', flag: '🇲🇽', dialCode: '52' },
    { code: 'CU', name: 'Cuba', flag: '🇨🇺', dialCode: '53' },
    { code: 'AR', name: 'Argentina', flag: '🇦🇷', dialCode: '54' },
    { code: 'BR', name: 'Brazil', flag: '🇧🇷', dialCode: '55' },
    { code: 'CL', name: 'Chile', flag: '🇨🇱', dialCode: '56' },
    { code: 'CO', name: 'Colombia', flag: '🇨🇴', dialCode: '57' },
    { code: 'VE', name: 'Venezuela', flag: '🇻🇪', dialCode: '58' },
    { code: 'MY', name: 'Malaysia', flag: '🇲🇾', dialCode: '60' },
    { code: 'AU', name: 'Australia', flag: '🇦🇺', dialCode: '61' },
    { code: 'ID', name: 'Indonesia', flag: '🇮🇩', dialCode: '62' },
    { code: 'PH', name: 'Philippines', flag: '🇵🇭', dialCode: '63' },
    { code: 'NZ', name: 'New Zealand', flag: '🇳🇿', dialCode: '64' },
    { code: 'SG', name: 'Singapore', flag: '🇸🇬', dialCode: '65' },
    { code: 'TH', name: 'Thailand', flag: '🇹🇭', dialCode: '66' },
    { code: 'JP', name: 'Japan', flag: '🇯🇵', dialCode: '81' },
    { code: 'KR', name: 'South Korea', flag: '🇰🇷', dialCode: '82' },
    { code: 'VN', name: 'Vietnam', flag: '🇻🇳', dialCode: '84' },
    { code: 'CN', name: 'China', flag: '🇨🇳', dialCode: '86' },
    { code: 'TR', name: 'Turkey', flag: '🇹🇷', dialCode: '90' },
    { code: 'IN', name: 'India', flag: '🇳', dialCode: '91' },
    { code: 'PK', name: 'Pakistan', flag: '🇵🇰', dialCode: '92' },
    { code: 'AF', name: 'Afghanistan', flag: '🇦🇫', dialCode: '93' },
    { code: 'LK', name: 'Sri Lanka', flag: '🇱🇰', dialCode: '94' },
    { code: 'MM', name: 'Myanmar', flag: '🇲🇲', dialCode: '95' },
    { code: 'IR', name: 'Iran', flag: '🇮🇷', dialCode: '98' },
    { code: 'MA', name: 'Morocco', flag: '🇲🇦', dialCode: '212' },
    { code: 'DZ', name: 'Algeria', flag: '🇩🇿', dialCode: '213' },
    { code: 'TN', name: 'Tunisia', flag: '🇹🇳', dialCode: '216' },
    { code: 'LY', name: 'Libya', flag: '🇱🇾', dialCode: '218' },
    { code: 'GM', name: 'Gambia', flag: '🇬🇲', dialCode: '220' },
    { code: 'SN', name: 'Senegal', flag: '🇸🇳', dialCode: '221' },
    { code: 'MR', name: 'Mauritania', flag: '🇲🇷', dialCode: '222' },
    { code: 'ML', name: 'Mali', flag: '🇲🇱', dialCode: '223' },
    { code: 'GN', name: 'Guinea', flag: '🇬🇳', dialCode: '224' },
    { code: 'CI', name: 'Ivory Coast', flag: '🇨🇮', dialCode: '225' },
    { code: 'BF', name: 'Burkina Faso', flag: '🇧🇫', dialCode: '226' },
    { code: 'NE', name: 'Niger', flag: '🇳🇪', dialCode: '227' },
    { code: 'TG', name: 'Togo', flag: '🇹🇬', dialCode: '228' },
    { code: 'BJ', name: 'Benin', flag: '🇧🇯', dialCode: '229' },
    { code: 'MU', name: 'Mauritius', flag: '🇲🇺', dialCode: '230' },
    { code: 'LR', name: 'Liberia', flag: '🇱🇷', dialCode: '231' },
    { code: 'SL', name: 'Sierra Leone', flag: '🇸🇱', dialCode: '232' },
    { code: 'GH', name: 'Ghana', flag: '🇬🇭', dialCode: '233' },
    { code: 'NG', name: 'Nigeria', flag: '🇳🇬', dialCode: '234' },
    { code: 'TD', name: 'Chad', flag: '🇹🇩', dialCode: '235' },
    { code: 'CF', name: 'Central African Republic', flag: '🇨🇫', dialCode: '236' },
    { code: 'CM', name: 'Cameroon', flag: '🇨🇲', dialCode: '237' },
    { code: 'CV', name: 'Cape Verde', flag: '🇨🇻', dialCode: '238' },
    { code: 'ST', name: 'Sao Tome and Principe', flag: '🇸🇹', dialCode: '239' },
    { code: 'GQ', name: 'Equatorial Guinea', flag: '🇬🇶', dialCode: '240' },
    { code: 'GA', name: 'Gabon', flag: '🇬🇦', dialCode: '241' },
    { code: 'CG', name: 'Republic of the Congo', flag: '🇨🇬', dialCode: '242' },
    { code: 'CD', name: 'Democratic Republic of the Congo', flag: '🇨🇩', dialCode: '243' },
    { code: 'AO', name: 'Angola', flag: '🇦🇴', dialCode: '244' },
    { code: 'GW', name: 'Guinea-Bissau', flag: '🇬🇼', dialCode: '245' },
    { code: 'IO', name: 'British Indian Ocean Territory', flag: '🇮🇴', dialCode: '246' },
    { code: 'SC', name: 'Seychelles', flag: '🇸🇨', dialCode: '248' },
    { code: 'SD', name: 'Sudan', flag: '🇸🇩', dialCode: '249' },
    { code: 'RW', name: 'Rwanda', flag: '🇷🇼', dialCode: '250' },
    { code: 'ET', name: 'Ethiopia', flag: '🇪🇹', dialCode: '251' },
    { code: 'SO', name: 'Somalia', flag: '🇸🇴', dialCode: '252' },
    { code: 'DJ', name: 'Djibouti', flag: '🇩🇯', dialCode: '253' },
    { code: 'KE', name: 'Kenya', flag: '🇰🇪', dialCode: '254' },
    { code: 'TZ', name: 'Tanzania', flag: '🇹🇿', dialCode: '255' },
    { code: 'UG', name: 'Uganda', flag: '🇺🇬', dialCode: '256' },
    { code: 'BI', name: 'Burundi', flag: '🇧🇮', dialCode: '257' },
    { code: 'MZ', name: 'Mozambique', flag: '🇲🇿', dialCode: '258' },
    { code: 'ZM', name: 'Zambia', flag: '🇿🇲', dialCode: '260' },
    { code: 'MG', name: 'Madagascar', flag: '🇲🇬', dialCode: '261' },
    { code: 'RE', name: 'Reunion', flag: '🇷🇪', dialCode: '262' },
    { code: 'ZW', name: 'Zimbabwe', flag: '🇿🇼', dialCode: '263' },
    { code: 'NA', name: 'Namibia', flag: '🇳🇦', dialCode: '264' },
    { code: 'MW', name: 'Malawi', flag: '🇲🇼', dialCode: '265' },
    { code: 'LS', name: 'Lesotho', flag: '🇱🇸', dialCode: '266' },
    { code: 'BW', name: 'Botswana', flag: '🇧🇼', dialCode: '267' },
    { code: 'SZ', name: 'Eswatini', flag: '🇸🇿', dialCode: '268' },
    { code: 'KM', name: 'Comoros', flag: '🇰🇲', dialCode: '269' },
    { code: 'SH', name: 'Saint Helena', flag: '🇸🇭', dialCode: '290' },
    { code: 'ER', name: 'Eritrea', flag: '🇪🇷', dialCode: '291' },
    { code: 'AW', name: 'Aruba', flag: '🇦🇼', dialCode: '297' },
    { code: 'FO', name: 'Faroe Islands', flag: '🇫🇴', dialCode: '298' },
    { code: 'GL', name: 'Greenland', flag: '🇬🇮', dialCode: '299' },
    { code: 'GI', name: 'Gibraltar', flag: '🇬🇮', dialCode: '350' },
    { code: 'PT', name: 'Portugal', flag: '🇵🇹', dialCode: '351' },
    { code: 'LU', name: 'Luxembourg', flag: '🇱🇺', dialCode: '352' },
    { code: 'IE', name: 'Ireland', flag: '🇮🇪', dialCode: '353' },
    { code: 'IS', name: 'Iceland', flag: '🇮🇸', dialCode: '354' },
    { code: 'AL', name: 'Albania', flag: '🇦🇱', dialCode: '355' },
    { code: 'MT', name: 'Malta', flag: '🇲🇹', dialCode: '356' },
    { code: 'CY', name: 'Cyprus', flag: '🇨🇾', dialCode: '357' },
    { code: 'FI', name: 'Finland', flag: '🇫🇮', dialCode: '358' },
    { code: 'BG', name: 'Bulgaria', flag: '🇧🇬', dialCode: '359' },
    { code: 'LT', name: 'Lithuania', flag: '🇱🇹', dialCode: '370' },
    { code: 'LV', name: 'Latvia', flag: '🇱🇻', dialCode: '371' },
    { code: 'EE', name: 'Estonia', flag: '🇪🇪', dialCode: '372' },
    { code: 'MD', name: 'Moldova', flag: '🇲🇩', dialCode: '373' },
    { code: 'AM', name: 'Armenia', flag: '🇦🇲', dialCode: '374' },
    { code: 'BY', name: 'Belarus', flag: '🇧🇾', dialCode: '375' },
    { code: 'AD', name: 'Andorra', flag: '🇦🇩', dialCode: '376' },
    { code: 'MC', name: 'Monaco', flag: '🇲🇨', dialCode: '377' },
    { code: 'SM', name: 'San Marino', flag: '🇸🇲', dialCode: '378' },
    { code: 'UA', name: 'Ukraine', flag: '🇺🇦', dialCode: '380' },
    { code: 'RS', name: 'Serbia', flag: '🇷🇸', dialCode: '381' },
    { code: 'ME', name: 'Montenegro', flag: '🇲🇪', dialCode: '382' },
    { code: 'HR', name: 'Croatia', flag: '🇭🇷', dialCode: '385' },
    { code: 'SI', name: 'Slovenia', flag: '🇸🇮', dialCode: '386' },
    { code: 'BA', name: 'Bosnia and Herzegovina', flag: '🇧🇦', dialCode: '387' },
    { code: 'MK', name: 'North Macedonia', flag: '🇲🇰', dialCode: '389' },
    { code: 'CZ', name: 'Czech Republic', flag: '🇨🇿', dialCode: '420' },
    { code: 'SK', name: 'Slovakia', flag: '🇸🇰', dialCode: '421' },
    { code: 'LI', name: 'Liechtenstein', flag: '🇱🇮', dialCode: '423' },
    { code: 'FK', name: 'Falkland Islands', flag: '🇫🇰', dialCode: '500' },
    { code: 'BZ', name: 'Belize', flag: '🇧🇿', dialCode: '501' },
    { code: 'GT', name: 'Guatemala', flag: '🇬🇹', dialCode: '502' },
    { code: 'SV', name: 'El Salvador', flag: '🇸🇻', dialCode: '503' },
    { code: 'HN', name: 'Honduras', flag: '🇭🇳', dialCode: '504' },
    { code: 'NI', name: 'Nicaragua', flag: '🇳🇮', dialCode: '505' },
    { code: 'CR', name: 'Costa Rica', flag: '🇨🇷', dialCode: '506' },
    { code: 'PA', name: 'Panama', flag: '🇵🇦', dialCode: '507' },
    { code: 'PM', name: 'Saint Pierre and Miquelon', flag: '🇵🇲', dialCode: '508' },
    { code: 'HT', name: 'Haiti', flag: '🇭🇹', dialCode: '509' },
    { code: 'GP', name: 'Guadeloupe', flag: '🇬🇵', dialCode: '590' },
    { code: 'BO', name: 'Bolivia', flag: '🇧🇴', dialCode: '591' },
    { code: 'GY', name: 'Guyana', flag: '🇬🇾', dialCode: '592' },
    { code: 'EC', name: 'Ecuador', flag: '🇪🇨', dialCode: '593' },
    { code: 'GF', name: 'French Guiana', flag: '🇬🇫', dialCode: '594' },
    { code: 'PY', name: 'Paraguay', flag: '🇵🇾', dialCode: '595' },
    { code: 'MQ', name: 'Martinique', flag: '🇲🇶', dialCode: '596' },
    { code: 'SR', name: 'Suriname', flag: '🇸🇷', dialCode: '597' },
    { code: 'UY', name: 'Uruguay', flag: '🇺🇾', dialCode: '598' },
    { code: 'CW', name: 'Curaçao', flag: '🇨🇼', dialCode: '599' },
    { code: 'TL', name: 'Timor-Leste', flag: '🇹🇱', dialCode: '670' },
    { code: 'AQ', name: 'Antarctica', flag: '🇦🇶', dialCode: '672' },
    { code: 'BN', name: 'Brunei', flag: '🇧🇳', dialCode: '673' },
    { code: 'NR', name: 'Nauru', flag: '🇳🇷', dialCode: '674' },
    { code: 'PG', name: 'Papua New Guinea', flag: '🇵🇬', dialCode: '675' },
    { code: 'TO', name: 'Tonga', flag: '🇹🇴', dialCode: '676' },
    { code: 'SB', name: 'Solomon Islands', flag: '🇸🇧', dialCode: '677' },
    { code: 'VU', name: 'Vanuatu', flag: '🇻🇺', dialCode: '678' },
    { code: 'FJ', name: 'Fiji', flag: '🇫🇯', dialCode: '679' },
    { code: 'PW', name: 'Palau', flag: '🇵🇼', dialCode: '680' },
    { code: 'WF', name: 'Wallis and Futuna', flag: '🇼🇫', dialCode: '681' },
    { code: 'CK', name: 'Cook Islands', flag: '🇨🇰', dialCode: '682' },
    { code: 'NU', name: 'Niue', flag: '🇳🇺', dialCode: '683' },
    { code: 'WS', name: 'Samoa', flag: '🇼🇸', dialCode: '685' },
    { code: 'KI', name: 'Kiribati', flag: '🇰🇮', dialCode: '686' },
    { code: 'NC', name: 'New Caledonia', flag: '🇳🇨', dialCode: '687' },
    { code: 'TV', name: 'Tuvalu', flag: '🇹🇻', dialCode: '688' },
    { code: 'PF', name: 'French Polynesia', flag: '🇵🇫', dialCode: '689' },
    { code: 'TK', name: 'Tokelau', flag: '🇹🇰', dialCode: '690' },
    { code: 'FM', name: 'Federated States of Micronesia', flag: '🇫🇲', dialCode: '691' },
    { code: 'MH', name: 'Marshall Islands', flag: '🇲🇭', dialCode: '692' },
    { code: 'KP', name: 'North Korea', flag: '🇰🇵', dialCode: '850' },
    { code: 'HK', name: 'Hong Kong', flag: '🇭🇰', dialCode: '852' },
    { code: 'MO', name: 'Macau', flag: '🇲🇴', dialCode: '853' },
    { code: 'KH', name: 'Cambodia', flag: '🇰🇭', dialCode: '855' },
    { code: 'LA', name: 'Laos', flag: '🇱🇦', dialCode: '856' },
    { code: 'BD', name: 'Bangladesh', flag: '🇧🇩', dialCode: '880' },
    { code: 'TW', name: 'Taiwan', flag: '🇹🇼', dialCode: '886' },
    { code: 'MV', name: 'Maldives', flag: '🇲🇻', dialCode: '960' },
    { code: 'LB', name: 'Lebanon', flag: '🇱🇧', dialCode: '961' },
    { code: 'JO', name: 'Jordan', flag: '🇯🇴', dialCode: '962' },
    { code: 'SY', name: 'Syria', flag: '🇸🇾', dialCode: '963' },
    { code: 'IQ', name: 'Iraq', flag: '🇮🇶', dialCode: '964' },
    { code: 'KW', name: 'Kuwait', flag: '🇰🇼', dialCode: '965' },
    { code: 'SA', name: 'Saudi Arabia', flag: '🇸🇦', dialCode: '966' },
    { code: 'YE', name: 'Yemen', flag: '🇾🇪', dialCode: '967' },
    { code: 'OM', name: 'Oman', flag: '🇴🇲', dialCode: '968' },
    { code: 'PS', name: 'Palestine', flag: '🇵🇸', dialCode: '970' },
    { code: 'AE', name: 'United Arab Emirates', flag: '🇦🇪', dialCode: '971' },
    { code: 'IL', name: 'Israel', flag: '🇮🇱', dialCode: '972' },
    { code: 'BH', name: 'Bahrain', flag: '🇧🇭', dialCode: '973' },
    { code: 'QA', name: 'Qatar', flag: '🇶🇦', dialCode: '974' },
    { code: 'BT', name: 'Bhutan', flag: '🇧🇹', dialCode: '975' },
    { code: 'MN', name: 'Mongolia', flag: '🇲🇳', dialCode: '976' },
    { code: 'NP', name: 'Nepal', flag: '🇳🇵', dialCode: '977' },
    { code: 'TJ', name: 'Tajikistan', flag: '🇹🇯', dialCode: '992' },
    { code: 'TM', name: 'Turkmenistan', flag: '🇹🇲', dialCode: '993' },
    { code: 'AZ', name: 'Azerbaijan', flag: '🇦🇿', dialCode: '994' },
    { code: 'GE', name: 'Georgia', flag: '🇬🇪', dialCode: '995' },
    { code: 'KG', name: 'Kyrgyzstan', flag: '🇰🇬', dialCode: '996' },
    { code: 'UZ', name: 'Uzbekistan', flag: '🇺🇿', dialCode: '998' },
  ];

  constructor() {
    // Establecer España como país por defecto
    this.selectedCountry =
      this.countries.find((country) => country.code === 'ES') || this.countries[0];

    // Inicializar países filtrados
    this.filteredCountries = [...this.countries];
  }

  ngAfterViewInit(): void {
    // Escuchar cambios en el input de teléfono
    if (this.phoneInput.valueChanges) {
      this.phoneInput.valueChanges.subscribe(() => {
        this.updatePhoneNumber();
      });
    }

    // Escuchar cambios en el select de país
    if (this.countrySelect) {
      this.countrySelect.valueChanges.subscribe(() => {
        this.updatePhoneNumber();
      });
    }
  }

  writeValue(value: string): void {
    if (value) {
      // Parsear el valor completo (ej: "34-666666666" o "34666666666")
      const parts = value.split('-');
      if (parts.length === 2) {
        const dialCode = parts[0];
        const number = parts[1];

        // Encontrar el país por dial code
        const country = this.countries.find((c) => c.dialCode === dialCode);
        if (country) {
          this.selectedCountry = country;
          this.phoneNumber = number;
          this.selectedCountryCode = country.code;
          // Usar writeValue de los componentes hijos
          if (this.countrySelect) {
            this.countrySelect.writeValue(country.code);
          }
          if (this.phoneInput) {
            this.phoneInput.writeValue(number);
          }
        }
      } else {
        // Si no tiene formato, asumir que es solo el número
        this.phoneNumber = value;
        if (this.phoneInput) {
          this.phoneInput.writeValue(value);
        }
      }
    } else {
      this.phoneNumber = '';
      this.selectedCountryCode = '';
      if (this.phoneInput) {
        this.phoneInput.writeValue('');
      }
      if (this.countrySelect) {
        this.countrySelect.writeValue('');
      }
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  private updatePhoneNumber(): void {
    const fullPhoneNumber = `${this.selectedCountry.dialCode}-${this.phoneNumber}`;
    this.onChange(fullPhoneNumber);
    this.phoneChange.emit(fullPhoneNumber);
  }

  onCountryChange(countryCode: string): void {
    this.selectedCountryCode = countryCode;
    this.selectedCountry =
      this.countries.find((country) => country.code === countryCode) || this.selectedCountry;
    this.updatePhoneNumber();
  }

  onPhoneNumberChange(value: string): void {
    this.phoneNumber = value;
    this.updatePhoneNumber();
  }

  // Métodos para la búsqueda de países
  onCountrySearchChange(query: string): void {
    this.countrySearchQuery = query;
    this.filterCountries();
  }

  onCountrySearchBlur(): void {
    // Cerrar el dropdown después de un pequeño delay para permitir clics
    setTimeout(() => {
      this.showCountryDropdown = false;
      // Limpiar búsqueda si no hay país seleccionado
      if (!this.selectedCountry) {
        this.countrySearchQuery = '';
      }
    }, 150);
  }

  onCountrySelect(country: CountryCode): void {
    this.selectedCountry = country;
    this.selectedCountryCode = country.code;
    this.countrySearchQuery = ''; // Limpiar búsqueda
    this.showCountryDropdown = false;
    this.updatePhoneNumber();
  }

  private filterCountries(): void {
    if (!this.countrySearchQuery) {
      this.filteredCountries = [...this.countries];
    } else {
      const query = this.countrySearchQuery.toLowerCase();
      this.filteredCountries = this.countries.filter(
        (country) =>
          country.name.toLowerCase().includes(query) ||
          country.code.toLowerCase().includes(query) ||
          country.dialCode.includes(query),
      );
    }
  }
}
